import * as React from 'react'
import {
  OutreachRepresentative,
  representativeLabels,
  representativeToLocationTypeMap
} from '../../constants/actions'
import { HBox } from '../../elements/basic-elements'
import OutreachIcon from '../../images/icons/outreach-icon'
import { LocationType } from '../../types/location-types'
import { removeDuplicates } from '../../utilities/array'
import { hasValue } from '../../utilities/data'

export enum CandidateCategories {
  ARE_ENDORSED_BY = 'are_endorsed_by',
  SUPPORT = 'support'
}

export const sanityString = (string: Queries.Maybe<string>) => {
  return string !== null ? string : ''
}

export const candidateVerbSlugs = ['support', 'donate', 'fundraise']
export const candidateNounSlugs = ['candidates']
export const isCandidateSlug = (slug?: string) => slug && candidateNounSlugs.includes(slug)

export const removeDuplicatesBySlug = (array: { slug: { current: string } }[]) => {
  const unique = removeDuplicates(array.map(item => item?.slug?.current))
  return unique.map(itemSlug => array.find(item => item?.slug?.current === itemSlug))
}

const defaults = { nonTextBehavior: 'remove' }

export const blocksToText = (blocks: any[], opts = {}) => {
  const options = Object.assign({}, defaults, opts)
  return (blocks || []).map(block => {
    if (block._type !== 'block' || !block.children) {
      return options.nonTextBehavior === 'remove' ? '' : `[${block._type} block]`
    }

    return block.children.map((child: any) => child?.text).join('')
  })
}

export const getUniqueCobenefits = (cobenefits: Queries.SanityCobenefits[]) => {
  if (!cobenefits) return
  const cleanCobenefits = cobenefits.filter(cobenefit => cobenefit?.slug?.current)
  const uniqueSlugs = Array.from(new Set(cleanCobenefits.map(({ slug }) => slug?.current)))
  return uniqueSlugs.map(currSlug => cleanCobenefits.find(({ slug }) => currSlug == slug?.current))
}

export const getSanitySlug = (slug?: Pick<Queries.Maybe<Queries.SanitySlug>, 'current'>) =>
  slug?.current && slug.current !== null && slug.current ? slug.current : undefined

export const getRepresentativeTagsFromOutreach = (
  outreach?: Queries.SanityOutreach
): LocationTag[] => {
  const repLabels =
    outreach?.targets?.reduce((labels: LocationTag[], target: OutreachRepresentative) => {
      const label = target !== OutreachRepresentative.OTHER && representativeLabels[target]
      if (label) {
        return [
          ...labels,
          {
            label: (
              <HBox gap={0.5} alignItems="center">
                <OutreachIcon size={12} /> {label}
              </HBox>
            ),
            type: representativeToLocationTypeMap[target]
          }
        ]
      }
    }, [] as LocationTag[]) || []

  const otherLabels =
    outreach?.targetsOther?.map(target => ({
      label: target.name,
      type: representativeToLocationTypeMap.other
    })) || []

  return [...repLabels, ...otherLabels]
}

type LocationTag = { label: React.ReactNode; type: LocationType }

export const getLocationTagsFromSanityLocation = (
  location?: Queries.SanityLocation
): LocationTag[] => {
  if (location?.cities && location.cities.length > 0) {
    return location.cities
      .filter(city => hasValue(city))
      .map(city => ({ label: city, type: 'city' }))
  }
  if (location?.states && location.states.length > 0) {
    return (location.states as string[])
      .filter(state => hasValue(state))
      .sort((a, b) => a.localeCompare(b))
      .map(state => ({
        label: state,
        type: 'state'
      }))
  }
  if (location?.countries && location.countries.length > 0) {
    return (location.countries as string[])
      .filter(country => hasValue(country))
      .sort((a, b) => a.localeCompare(b))
      .map(country => ({ label: country, type: 'federal' }))
  }
}

export const getLocationType = (
  location?: Queries.Maybe<Queries.SanityLocation>
): LocationType | undefined => {
  if (!location?.isRegional) {
    return 'global'
  } else {
    if (location?.cities && location?.cities.length > 0) {
      return 'local'
    }
    if (location?.states && location?.states.length > 0) {
      return 'state'
    }
    if (location?.countries && location?.countries.length > 0) {
      return 'federal'
    }
  }
}
